import { InputAdornment } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CancelIcon from '@material-ui/icons/Cancel';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Select from 'react-select';
import languageValues from 'utils/LanguageUtils';
import { getMergedClassNames } from '../../../utils/HTMLUtils';
import CheckSquareIcon from '../../Icons/CheckSquareIcon';
import RefreshIcon from '../../Icons/RefreshIcon';
import SquareIcon from '../../Icons/SquareIcon';
import TooltipCaller from '../../TooltipManager/TooltipCaller';
import { CustomTextField } from './../../CustomTextField';
import styles from './NewAutoCompleteFieldEstados.scss';
const rootClassName = styles.rootClassName;

const useStyles = makeStyles((theme) => ({
  input: {
    display: 'flex',
    padding: 0,
    height: 'auto',
  },
  valueContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
    overflow: 'hidden',
    gap: '0 4px',
  },
  noOptionsMessage: {
    padding: theme.spacing(1, 2),
  },
  singleValue: {
    fontSize: 16,
  },
  placeholder: {
    fontSize: 16,
  },
  paper: {
    position: 'absolute',
    zIndex: 99999,
    marginTop: theme.spacing(1),
    left: 0,
    right: 0,
  },
  divider: {
    height: theme.spacing(2),
  },
}));

function NoOptionsMessage(props) {
  return (
    <Typography
      color='textSecondary'
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

NoOptionsMessage.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  selectProps: PropTypes.object.isRequired,
};

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />;
}

inputComponent.propTypes = {
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

function Control(props) {
  const {
    children,
    innerProps,
    innerRef,
    selectProps: { classes, TextFieldProps },
  } = props;

  return (
    <CustomTextField
      fullWidth
      className={getMergedClassNames([rootClassName + '-input'])}
      isAutoComplete={true}
      InputProps={{
        inputComponent,
        inputProps: {
          className: classes.input,
          ref: innerRef,
          children,
          ...innerProps,
        },
      }}
      {...TextFieldProps}
    />
  );
}

Control.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  selectProps: PropTypes.object.isRequired,
};

function Option(props) {
  function getText() {
    let label = props.children;
    label = label
      .toUpperCase()
      .split(props.inputValue.trim().toUpperCase())
      .join('<b>' + props.inputValue.trim().toUpperCase() + '</b>');
    return <div dangerouslySetInnerHTML={{ __html: label }} />;
  }

  function getIcons() {
    if (!props.isMulti) {
      return null;
    }
    return props.isSelected ? (
      <CheckSquareIcon className={rootClassName + '-item-selected-checkbox'} />
    ) : (
      <SquareIcon className={rootClassName + '-item-unselected-checkbox'} />
    );
  }

  function valueIsEqual(value1, value2) {
    const strValue1 = typeof value1 !== 'string' ? String(value1) : value1;
    const strValue2 = typeof value2 !== 'string' ? String(value2) : value2;
    return strValue1.trim().toLowerCase() === strValue2.trim().toLowerCase()
      ? true
      : false;
  }

  function isSelected() {
    const currValue = props.getValue();
    if (typeof currValue === 'object' && currValue.length > 0) {
      return currValue.filter((item) => valueIsEqual(item.value, props.value))
        .length > 0
        ? true
        : false;
    }
    return false;
  }

  return (
    <MenuItem
      ref={props.innerRef}
      selected={isSelected()}
      component='div'
      {...props.innerProps}
    >
      {getIcons()}
      {getText()}
    </MenuItem>
  );
}

Option.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  isFocused: PropTypes.bool,
  isSelected: PropTypes.bool,
};

function Placeholder(props) {
  return (
    <Typography
      color='textSecondary'
      className={props.selectProps.classes.placeholder}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

Placeholder.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  selectProps: PropTypes.object.isRequired,
};

function SingleValue(props) {
  return (
    <Typography
      className={getMergedClassNames([
        props.selectProps.classes.singleValue,
        rootClassName + '-single-value',
      ])}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

SingleValue.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  selectProps: PropTypes.object.isRequired,
};

function ValueContainer(props) {
  return (
    <div className={props.selectProps.classes.valueContainer}>
      {props.children}
    </div>
  );
}

ValueContainer.propTypes = {
  children: PropTypes.node,
  selectProps: PropTypes.object.isRequired,
};

function MultiValue(props) {
  return (
    <Chip
      color={props.isDisabled ? 'default' : 'secondary'}
      tabIndex={-1}
      label={props.children}
      className={clsx(props.selectProps.classes.chip, {
        [props.selectProps.classes.chipFocused]: props.isFocused,
      })}
      onDelete={props.isDisabled ? null : props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
    />
  );
}

MultiValue.propTypes = {
  children: PropTypes.node,
  isFocused: PropTypes.bool,
  removeProps: PropTypes.object.isRequired,
  selectProps: PropTypes.object.isRequired,
};

function NewAutoCompleteFieldElement(props) {
  let inputValue = '';

  const uniqueClassName = props.uniqueComponentClassName;

  const classes = useStyles();
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);

  function getDefaultValue() {
    if (isMultiple()) {
      return props.defaultValue
        ? props.defaultValue.map((value) => {
            const filteredValues = props.options.filter((option) =>
              option.value === value ? option : null
            );
            return filteredValues && filteredValues.length > 0
              ? filteredValues[0]
              : null;
          })
        : [];
    } else {
      const filteredValue = props.options.filter((option) =>
        option.value === props.defaultValue ? true : false
      );
      return filteredValue.length > 0 ? filteredValue[0] : [];
    }
  }

  function getValue() {
    if (isMultiple()) {
      return props.value.map((value) => {
        const filteredValues = props.options.filter((option) =>
          option.value === value ? option : null
        );
        return filteredValues && filteredValues.length > 0
          ? filteredValues[0]
          : null;
      });
    } else {
      const filteredValue = props.options.filter((option) =>
        option.value === props.value ? true : false
      );
      return filteredValue.length > 0 ? filteredValue[0] : [];
    }
  }

  function isMultiple() {
    return props.multiple || false;
  }

  function getSelectClassStr() {
    return isMultiple() ? 'react-select-multiple' : 'react-select-single';
  }

  function onChange(value) {
    if (typeof props.onChange === 'function') {
      props.onChange(value);
    }
  }

  const selectStyles = {
    input: (base) => ({
      ...base,
      color: theme.palette.text.primary,
      '& input': {
        font: 'inherit',
      },
    }),
  };

  const filterOption = ({ data, label }, query) => {
    const disabled = data && data.disabled ? true : false;
    return !disabled && label.toLowerCase().indexOf(query.toLowerCase()) >= 0;
  };

  if (
    props.loadError &&
    props.loadErrorMessage &&
    props.loadErrorHint &&
    typeof props.reloadCallback === 'function'
  ) {
    return (
      <CustomTextField
        id={props.id}
        label={props.label || ''}
        disabled={true}
        helperText={props.helperText || ''}
        value={props.loadErrorMessage}
        className={getMergedClassNames([
          uniqueClassName,
          rootClassName + '-select',
          props.className || '',
          rootClassName + '-input',
          rootClassName + '-input-load-error',
        ])}
        InputProps={{
          inputProps: {
            className: classes.input,
          },
          endAdornment: (
            <InputAdornment position='start'>
              <TooltipCaller text={props.loadErrorHint}>
                <button
                  id={props.id + '-reload-button'}
                  type='button'
                  onClick={props.reloadCallback}
                  className={rootClassName + '-reload-button'}
                >
                  <RefreshIcon static />
                </button>
              </TooltipCaller>
            </InputAdornment>
          ),
        }}
      />
    );
  }

  return props.loading ? (
    <CustomTextField
      id={props.id}
      label={props.label || ''}
      placeholder={
        props.loadingMessage ? props.loadingMessage : props.placeholder ?? ''
      }
      disabled={true}
      helperText={props.helperText || ''}
      value={props.loadingMessage || ''}
      error={props.error || false}
      className={getMergedClassNames([
        uniqueClassName,
        rootClassName + '-select',
        props.className || '',
        rootClassName + '-input',
        props.loading ? rootClassName + '-input-loading' : '',
      ])}
      InputProps={{
        inputProps: {
          className: classes.input,
        },
      }}
    />
  ) : (
    <Select
      id={props.id}
      classNamePrefix={rootClassName}
      backspaceRemovesValue={true}
      isClearable={true}
      isSearchable
      closeMenuOnSelect={isMultiple() ? false : true}
      onInputChange={(value) => {
        inputValue = value;
      }}
      filterOption={filterOption}
      classes={classes}
      styles={selectStyles}
      className={getMergedClassNames([
        uniqueClassName,
        rootClassName + '-select',
        props.className || '',
      ])}
      inputId={rootClassName + ''}
      TextFieldProps={{
        label: props.label || '',
        InputLabelProps: {
          htmlFor: getSelectClassStr(),
          shrink: true,
        },
        disabled: props.disabled,
        helperText: props.helperText || '',
        error: props.error || false,
        onKeyDown: (event) => {
          inputValue = event.target.value;
        },
      }}
      placeholder={
        props.loading && props.loadingMessage
          ? props.loadingMessage
          : props.placeholder ?? ''
      }
      options={props.options ?? []}
      noOptionsMessage={() => {
        return (props.options || []).length === 0
          ? languageValues.components.newAutoCompleteField.emptyMessage
          : languageValues.components.newAutoCompleteField.notFoundMessage;
      }}
      components={{
        Control,
        MultiValue,
        NoOptionsMessage,
        Option: function (newProps) {
          newProps = {
            ...newProps,
            inputValue: inputValue,
          };
          return <Option {...newProps}>{newProps.children}</Option>;
        },
        Placeholder,
        SingleValue,
        ValueContainer,
      }}
      defaultValue={getDefaultValue()}
      value={getValue()}
      onChange={onChange}
      isMulti={isMultiple()}
      hideSelectedOptions={false}
      isDisabled={props.disabled}
      menuIsOpen={props.disabled ? false : open}
      onMenuOpen={() => setOpen(true)}
      onMenuClose={() => setOpen(false)}
    />
  );
}

NewAutoCompleteFieldElement.propTypes = {
  options: PropTypes.array.isRequired,
  id: PropTypes.string,
  className: PropTypes.string,
  uniqueComponentClassName: PropTypes.string,
  helperText: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  loadError: PropTypes.bool,
  loadErrorMessage: PropTypes.string,
  loadErrorHint: PropTypes.string,
  reloadCallback: PropTypes.func,
  error: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  placeholder: PropTypes.string,
  loadingMessage: PropTypes.string,
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  multiple: PropTypes.bool,
};

export class NewAutoCompleteFieldEstados extends Component {
  constructor(props) {
    super(props);
    this.state = {
      uniqueComponentClassName: rootClassName + '-id-',
    };
  }

  render() {
    return this.state.uniqueComponentClassName ? (
      <NewAutoCompleteFieldElement
        {...this.props}
        uniqueComponentClassName={this.state.uniqueComponentClassName}
      />
    ) : null;
  }
}

export default NewAutoCompleteFieldEstados;
